// Looper — multi-layer recording on the continuous Transport (docs/04 §4.5).
//
// The FIRST layer establishes the loop phase (count-in pre-roll + restart);
// later layers OVERDUB against the running loop. Event layers re-fire an
// instrument; audio layers (mic) record a buffer. Each layer owns its mixer
// channel; layers serialize for persistence (docs/08 §8.3).

import * as Tone from "tone";
import { createInstrument, getInstrumentDef } from "../instruments/registry.ts";
import type { Instrument } from "../instruments/types.ts";
import type { AudioRecorder } from "../input/mic.ts";
import type { SerializedLayer } from "../state/persistence.ts";
import type { Id, NoteEvent, QuantizeGrid, ScaleName } from "../util/types.ts";
import { AudioLayer, EventLayer } from "./layer.ts";
import type { Mixer } from "./mixer.ts";
import { gridToSeconds, quantizeEvents } from "./quantize.ts";
import type { TransportController } from "./transport.ts";
import { multipleLoopEnd, snapDurationToBars, type LayerMultiple } from "../util/loop.ts";

export const MIC_ID = "mic";
export const MAX_LAYERS = 16;
const VOICE_COLOR = "#f78fb3";
const LOOP_FIRST_MAX_BARS = 8;
export type LoopMode = "tempo-first" | "loop-first";

export type LooperState = "idle" | "counting" | "recording";

export interface LayerSummary {
  id: Id;
  name: string;
  color: string;
  kind: "event" | "audio";
  muted: boolean;
  solo: boolean;
  locked: boolean;
  multiple: number; // loop-length multiple (1/2/4)
  volumeDb: number;
  pan: number;
  sendAmount: number;
  marks: ReadonlyArray<{ pos: number; velocity: number }>;
}

export interface LooperCallbacks {
  onState(state: LooperState): void;
  onCountIn(beatsRemaining: number): void;
  onLayersChanged(layers: LayerSummary[]): void;
  onCapReached?(): void;
  onLoopBarsChanged?(bars: number): void;
}

export interface PersistenceHooks {
  saveBlob(ref: string, blob: Blob): void;
  deleteBlob(ref: string): void;
}

interface LayerHandle {
  id: Id;
  name: string;
  color: string;
  kind: "event" | "audio";
  runtime: EventLayer | AudioLayer;
  muted: boolean;
  solo: boolean;
  locked: boolean;
  volumeDb: number;
  pan: number;
  sendAmount: number;
  multiple: LayerMultiple; // 1 = base loop; 2/4 = longer layer
  // event only
  instrument?: Instrument;
  instrumentId?: string;
  events?: NoteEvent[];
  // audio only
  audioRef?: string;
  durationSec?: number;
  peaks?: number[];
}

export class Looper {
  private state: LooperState = "idle";
  private events: NoteEvent[] = [];
  private readonly layers: LayerHandle[] = [];

  private recordingInstrumentId: string | null = null;
  private recordingMic: AudioRecorder | null = null;
  private loopUnsub: (() => void) | null = null;
  private countdownUnsub: (() => void) | null = null;
  private freshStartId: number | null = null;
  private loopsUntilRecord = 1;
  private lastRecordedCount = 0;

  private key = "C";
  private scale: ScaleName = "majorPentatonic";

  private countInBars: 0 | 1 | 2 = 1;
  private quantizeOn = true;
  private grid: QuantizeGrid = "16n";
  private strength = 0.9;

  private audioRecorder: AudioRecorder | null = null;
  private latencyOffsetMs = 120;
  private persistence: PersistenceHooks | null = null;

  private loopMode: LoopMode = "tempo-first";
  private nextMultiple: LayerMultiple = 1;
  private recordingMultiple: LayerMultiple = 1;
  private multiplePass = 0;
  private loopFirstActive = false;

  constructor(
    private readonly transport: TransportController,
    private readonly mixer: Mixer,
    private readonly cb: LooperCallbacks,
  ) {}

  setCountInBars(n: 0 | 1 | 2): void {
    this.countInBars = n;
  }
  setQuantize(on: boolean): void {
    this.quantizeOn = on;
  }
  setGrid(grid: QuantizeGrid): void {
    this.grid = grid;
  }
  setStrength(strength: number): void {
    this.strength = strength;
  }
  setAudioRecorder(rec: AudioRecorder | null): void {
    this.audioRecorder = rec;
  }
  setLatencyOffsetMs(ms: number): void {
    this.latencyOffsetMs = ms;
  }
  setPersistence(hooks: PersistenceHooks): void {
    this.persistence = hooks;
  }
  setLoopMode(mode: LoopMode): void {
    this.loopMode = mode;
  }
  setNextMultiple(m: LayerMultiple): void {
    this.nextMultiple = m;
  }

  setScale(key: string, scale: ScaleName): void {
    this.key = key;
    this.scale = scale;
    for (const layer of this.layers) layer.instrument?.setScale?.(key, scale);
  }

  get currentState(): LooperState {
    return this.state;
  }
  get layerCount(): number {
    return this.layers.length;
  }
  get recordedCount(): number {
    return this.lastRecordedCount;
  }
  /** Current capture position, including the pass offset for multiple-length layers. */
  loopTimeSec(): number {
    const base = this.transport.loopDurationSec;
    return this.multiplePass * base + this.transport.progress * base;
  }

  /** Keyboard/touch one-shot: record at the current loop position. */
  capture(ev: NoteEvent): void {
    if (this.state !== "recording") return;
    this.events.push({ ...ev, time: this.loopTimeSec() });
  }

  /** Archetype event whose `time` is already loop-relative (from loopTimeSec). */
  recordEvent(ev: NoteEvent): void {
    if (this.state !== "recording") return;
    this.events.push({ ...ev });
  }

  record(instrumentId: string): void {
    const isMic = instrumentId === MIC_ID;
    // In loop-first, a second Record press ends the open first take.
    if (this.loopFirstActive && this.state === "recording") {
      this.stopLoopFirst();
      return;
    }
    if (this.state !== "idle") return;
    if (this.layers.length >= MAX_LAYERS) {
      this.cb.onCapReached?.();
      return;
    }
    if (isMic && !this.audioRecorder) return; // mic not open
    this.recordingInstrumentId = instrumentId;
    this.recordingMic = isMic ? this.audioRecorder : null;
    this.events = [];
    this.multiplePass = 0;
    // Multiples apply to event overdubs only (not the first layer or the mic).
    this.recordingMultiple = this.layers.length > 0 && !isMic ? this.nextMultiple : 1;

    if (this.loopMode === "loop-first" && this.layers.length === 0 && !isMic) {
      this.startLoopFirst();
    } else if (this.layers.length === 0) {
      this.startFresh();
    } else {
      this.startOverdub();
    }
  }

  // --- First layer: define loop phase with a count-in pre-roll -------------
  private startFresh(): void {
    const beatSec = 60 / this.transport.getBpm();
    const beatsPerBar = this.transport.beatsPerBar;
    const countInBeats = this.countInBars * beatsPerBar;
    const lead = 0.12;

    this.transport.stop();
    this.transport.resetPosition();
    this.setState("counting");

    const t0 = Tone.now() + lead;
    for (let i = 0; i < countInBeats; i++) {
      const clickTime = t0 + i * beatSec;
      this.transport.countClickAt(i % beatsPerBar === 0, clickTime);
      const remaining = countInBeats - i;
      Tone.getDraw().schedule(() => this.cb.onCountIn(remaining), clickTime);
    }

    const recordStart = Math.max(t0 + countInBeats * beatSec, Tone.now() + beatSec + lead);
    this.transport.startAt(recordStart, 0);
    this.freshStartId = this.transport.scheduleOnce((time) => {
      this.freshStartId = null;
      this.startCapture(time);
    }, 0);
    this.loopUnsub = this.transport.onLoopOnce((time) => this.completeCapture(time));
  }

  // --- Loop-first: the first take's length defines the loop (docs/04 §4.3) -
  private startLoopFirst(): void {
    // Provisional long loop so it doesn't wrap during the take.
    this.transport.setBars(LOOP_FIRST_MAX_BARS);
    this.loopFirstActive = true;
    const beatSec = 60 / this.transport.getBpm();
    const beatsPerBar = this.transport.beatsPerBar;
    const countInBeats = this.countInBars * beatsPerBar;
    const lead = 0.12;

    this.transport.stop();
    this.transport.resetPosition();
    this.setState("counting");

    const t0 = Tone.now() + lead;
    for (let i = 0; i < countInBeats; i++) {
      const clickTime = t0 + i * beatSec;
      this.transport.countClickAt(i % beatsPerBar === 0, clickTime);
      const remaining = countInBeats - i;
      Tone.getDraw().schedule(() => this.cb.onCountIn(remaining), clickTime);
    }
    const recordStart = Math.max(t0 + countInBeats * beatSec, Tone.now() + beatSec + lead);
    this.transport.startAt(recordStart, 0);
    this.freshStartId = this.transport.scheduleOnce((time) => {
      this.freshStartId = null;
      this.startCapture(time);
    }, 0);
    // No auto-stop; the user presses Record again → stopLoopFirst().
  }

  private stopLoopFirst(): void {
    this.loopFirstActive = false;
    this.clearWatchers();
    const provisionalDur = this.transport.loopDurationSec;
    const elapsed = this.transport.progress * provisionalDur;
    const bars = Math.min(LOOP_FIRST_MAX_BARS, snapDurationToBars(elapsed, this.transport.getBpm(), this.transport.beatsPerBar));
    this.transport.setBars(bars);
    this.state = "idle";
    this.recordingMultiple = 1;
    this.finalizeEvents(Tone.now());
    this.cb.onLoopBarsChanged?.(bars);
  }

  // --- Overdub: align to the running loop, no restart ---------------------
  private startOverdub(): void {
    const beatsPerBar = this.transport.beatsPerBar;
    const totalLoopBeats = this.transport.getBars() * beatsPerBar;
    const minCountInBeats = this.countInBars * beatsPerBar;
    const beatsLeft = totalLoopBeats - this.transport.currentLoopBeats();
    this.loopsUntilRecord = this.countInBars > 0 && beatsLeft < minCountInBeats ? 2 : 1;

    this.setState("counting");
    const countWindow = Math.max(1, this.countInBars) * beatsPerBar;
    this.countdownUnsub = this.transport.onBeat(() => {
      const remaining = this.beatsUntilRecord(totalLoopBeats);
      if (remaining <= countWindow) this.cb.onCountIn(remaining);
    });
    this.loopUnsub = this.transport.onLoop((time) => this.onOverdubBoundary(time));
  }

  private beatsUntilRecord(totalLoopBeats: number): number {
    const beatsLeft = totalLoopBeats - this.transport.currentLoopBeats();
    return Math.max(1, Math.ceil(beatsLeft + (this.loopsUntilRecord - 1) * totalLoopBeats));
  }

  private onOverdubBoundary(time: number): void {
    if (this.state === "counting") {
      this.loopsUntilRecord -= 1;
      if (this.loopsUntilRecord <= 0) {
        this.countdownUnsub?.();
        this.countdownUnsub = null;
        this.startCapture(time);
      }
    } else if (this.state === "recording") {
      // For a multiple-length layer, capture spans several base loops.
      this.multiplePass += 1;
      if (this.multiplePass >= this.recordingMultiple) this.completeCapture(time);
    }
  }

  // --- Capture lifecycle (shared by event + audio) ------------------------
  private startCapture(time: number): void {
    this.state = "recording";
    this.events = [];
    this.multiplePass = 0;
    this.recordingMic?.startRecording();
    Tone.getDraw().schedule(() => this.cb.onState("recording"), time);
  }

  private completeCapture(time: number): void {
    this.state = "idle";
    this.clearWatchers();
    if (this.recordingMic) this.finalizeAudio(time);
    else this.finalizeEvents(time);
  }

  private finalizeEvents(time: number): void {
    const multiple = this.recordingMultiple;
    const loopDur = this.transport.loopDurationSec * multiple;
    const gridSec = gridToSeconds(this.grid, this.transport.getBpm());
    const strength = this.quantizeOn ? this.strength : 0;
    const quantized = quantizeEvents(this.events, gridSec, strength, loopDur);
    this.lastRecordedCount = quantized.length;

    if (quantized.length > 0 && this.recordingInstrumentId) {
      this.addEventLayer(this.recordingInstrumentId, quantized, multiple);
    }
    // Lane list is a DOM update — notify directly (works even when rAF/Draw is
    // throttled); only the "idle" state cue is synced to the loop boundary.
    this.cb.onLayersChanged(this.summaries());
    Tone.getDraw().schedule(() => this.cb.onState("idle"), time);
  }

  private finalizeAudio(time: number): void {
    const rec = this.recordingMic;
    this.recordingMic = null;
    const loopDur = this.transport.loopDurationSec;
    Tone.getDraw().schedule(() => this.cb.onState("idle"), time);
    if (!rec) return;
    // Stop a little after the loop boundary so the tail (shifted late by
    // latency) is captured, then build the layer.
    window.setTimeout(() => void this.buildAudioFromRecorder(rec, loopDur), this.latencyOffsetMs + 200);
  }

  private async buildAudioFromRecorder(rec: AudioRecorder, loopDur: number): Promise<void> {
    let blob: Blob;
    try {
      blob = await rec.stopRecording();
    } catch {
      this.cb.onLayersChanged(this.summaries());
      return;
    }
    if (blob.size > 0) {
      try {
        await this.addAudioLayer(blob, loopDur);
      } catch {
        // decode/build failed — leave the loop unchanged
      }
    }
    this.cb.onLayersChanged(this.summaries());
  }

  private addEventLayer(instrumentId: string, events: NoteEvent[], multiple: LayerMultiple): void {
    const id = crypto.randomUUID();
    const def = getInstrumentDef(instrumentId);
    const instrument = createInstrument(instrumentId);
    const channel = this.mixer.addChannel(id);
    instrument.init(channel);
    instrument.setScale?.(this.key, this.scale);

    const loopEnd = multiple > 1 ? multipleLoopEnd(this.transport.getBars(), multiple) : this.transport.loopEndPosition;
    const runtime = new EventLayer(instrument, events, loopEnd);
    runtime.start(0);
    const suffix = multiple > 1 ? ` ${multiple}×` : "";
    this.layers.push({
      id, name: def.name + suffix, color: def.color, kind: "event", multiple,
      runtime, instrument, instrumentId, events,
      muted: false, solo: false, locked: false, volumeDb: 0, pan: 0, sendAmount: 0,
    });
  }

  private async addAudioLayer(blob: Blob, loopDur: number): Promise<void> {
    const id = crypto.randomUUID();
    const channel = this.mixer.addChannel(id);
    const { layer, peaks, durationSec } = await AudioLayer.fromBlob(blob, {
      loopDurSec: loopDur,
      latencyOffsetSec: this.latencyOffsetMs / 1000,
      output: channel,
    });
    layer.start(0);
    const audioRef = `audio:${id}`;
    this.persistence?.saveBlob(audioRef, blob);
    const voiceNum = this.layers.filter((l) => l.kind === "audio").length + 1;
    this.layers.push({
      id, name: `Voice ${voiceNum}`, color: VOICE_COLOR, kind: "audio", multiple: 1,
      runtime: layer, audioRef, durationSec, peaks,
      muted: false, solo: false, locked: false, volumeDb: 0, pan: 0, sendAmount: 0,
    });
  }

  setLayerMute(id: Id, muted: boolean): void {
    const layer = this.layers.find((l) => l.id === id);
    if (!layer) return;
    layer.muted = muted;
    this.mixer.setMute(id, muted);
    this.cb.onLayersChanged(this.summaries());
  }

  setLayerVolume(id: Id, db: number): void {
    const layer = this.layers.find((l) => l.id === id);
    if (!layer) return;
    layer.volumeDb = db;
    this.mixer.setVolume(id, db);
  }

  setLayerSolo(id: Id, solo: boolean): void {
    const layer = this.layers.find((l) => l.id === id);
    if (!layer) return;
    layer.solo = solo;
    this.mixer.setSolo(id, solo);
    this.cb.onLayersChanged(this.summaries());
  }

  /** Lock a layer to protect it from delete, clear, and timing edits. */
  setLayerLock(id: Id, locked: boolean): void {
    const layer = this.layers.find((l) => l.id === id);
    if (!layer) return;
    layer.locked = locked;
    this.cb.onLayersChanged(this.summaries());
  }

  /** Move a layer to a new index in the stack (visual/organisational order). */
  reorderLayer(id: Id, toIndex: number): void {
    const from = this.layers.findIndex((l) => l.id === id);
    if (from < 0) return;
    const clamped = Math.max(0, Math.min(this.layers.length - 1, toIndex));
    if (from === clamped) return;
    const [moved] = this.layers.splice(from, 1);
    if (moved) this.layers.splice(clamped, 0, moved);
    this.cb.onLayersChanged(this.summaries());
  }

  setLayerPan(id: Id, pan: number): void {
    const layer = this.layers.find((l) => l.id === id);
    if (!layer) return;
    layer.pan = pan;
    this.mixer.setPan(id, pan);
  }

  setLayerSend(id: Id, amount: number): void {
    const layer = this.layers.find((l) => l.id === id);
    if (!layer) return;
    layer.sendAmount = amount;
    this.mixer.setSend(id, amount);
  }

  renameLayer(id: Id, name: string): void {
    const layer = this.layers.find((l) => l.id === id);
    if (!layer) return;
    layer.name = name;
    this.cb.onLayersChanged(this.summaries());
  }

  /** Move one event of a layer to a new loop position (0..1), then rebuild it. */
  moveEvent(id: Id, index: number, pos: number): void {
    const layer = this.layers.find((l) => l.id === id);
    if (!layer || layer.locked || layer.kind !== "event" || !layer.events || !layer.instrument) return;
    const ev = layer.events[index];
    if (!ev) return;
    const loopDur = this.transport.loopDurationSec * layer.multiple;
    let time = Math.max(0, Math.min(0.999, pos)) * loopDur;
    if (this.quantizeOn) {
      const grid = gridToSeconds(this.grid, this.transport.getBpm());
      if (grid > 0) time = Math.round(time / grid) * grid;
    }
    time = ((time % loopDur) + loopDur) % loopDur;
    layer.events[index] = { ...ev, time };

    const loopEnd = layer.multiple > 1 ? multipleLoopEnd(this.transport.getBars(), layer.multiple) : this.transport.loopEndPosition;
    layer.runtime.dispose();
    const runtime = new EventLayer(layer.instrument, layer.events, loopEnd);
    runtime.start(0);
    layer.runtime = runtime;
    this.cb.onLayersChanged(this.summaries());
  }

  /** Live 0..1 level for a layer (from the mixer meter), for visuals. */
  layerLevel(id: Id): number {
    return this.mixer.getLevel(id);
  }

  layerIds(): Id[] {
    return this.layers.map((l) => l.id);
  }

  // `keepBlob` leaves the audio blob in storage so an undo can restore it; the
  // app purges orphaned blobs once the undo is superseded.
  deleteLayer(id: Id, keepBlob = false): void {
    const idx = this.layers.findIndex((l) => l.id === id);
    if (idx < 0 || this.layers[idx]?.locked) return; // locked layers are protected
    const [layer] = this.layers.splice(idx, 1);
    if (layer) this.disposeLayer(layer, keepBlob);
    this.cb.onLayersChanged(this.summaries());
  }

  deleteLastLayer(): void {
    // Skip locked layers so a stray undo/gesture can't wipe a protected take.
    const last = [...this.layers].reverse().find((l) => !l.locked);
    if (last) this.deleteLayer(last.id);
  }

  /** Clear all layers except locked ones. */
  clear(keepBlobs = false): void {
    this.cancelRecording();
    const kept: LayerHandle[] = [];
    for (const layer of this.layers) {
      if (layer.locked) kept.push(layer);
      else this.disposeLayer(layer, keepBlobs);
    }
    this.layers.length = 0;
    this.layers.push(...kept);
    this.events = [];
    this.setState("idle");
    this.cb.onLayersChanged(this.summaries());
  }

  /** Permanently delete the given audio blob refs (undo cleanup). */
  purgeBlobs(refs: readonly string[]): void {
    for (const ref of refs) this.persistence?.deleteBlob(ref);
  }

  /** Clone a layer (event = instant; audio = copies the blob). */
  async duplicateLayer(id: Id, loadBlob: (ref: string) => Promise<Blob | null>): Promise<void> {
    const src = this.layers.find((l) => l.id === id);
    if (!src) return;
    if (this.layers.length >= MAX_LAYERS) {
      this.cb.onCapReached?.();
      return;
    }
    const idx = this.layers.indexOf(src);
    const newId = crypto.randomUUID();
    const channel = this.mixer.addChannel(newId);
    let handle: LayerHandle | null = null;

    if (src.kind === "event" && src.events && src.instrumentId) {
      const instrument = createInstrument(src.instrumentId);
      instrument.init(channel);
      instrument.setScale?.(this.key, this.scale);
      const loopEnd = src.multiple > 1 ? multipleLoopEnd(this.transport.getBars(), src.multiple) : this.transport.loopEndPosition;
      const events = src.events.map((e) => ({ ...e }));
      const runtime = new EventLayer(instrument, events, loopEnd);
      runtime.start(0);
      handle = {
        id: newId, name: `${src.name} copy`, color: src.color, kind: "event", multiple: src.multiple,
        runtime, instrument, instrumentId: src.instrumentId, events,
        muted: src.muted, solo: src.solo, locked: false, volumeDb: src.volumeDb, pan: src.pan, sendAmount: src.sendAmount,
      };
    } else if (src.kind === "audio" && src.audioRef) {
      const blob = await loadBlob(src.audioRef);
      if (!blob) {
        this.mixer.removeChannel(newId);
        return;
      }
      const newRef = `audio:${newId}`;
      this.persistence?.saveBlob(newRef, blob);
      const { layer, peaks, durationSec } = await AudioLayer.fromBlob(blob, {
        loopDurSec: this.transport.loopDurationSec,
        latencyOffsetSec: this.latencyOffsetMs / 1000,
        output: channel,
      });
      layer.start(0);
      handle = {
        id: newId, name: `${src.name} copy`, color: src.color, kind: "audio", multiple: 1,
        runtime: layer, audioRef: newRef, durationSec, peaks,
        muted: src.muted, solo: src.solo, locked: false, volumeDb: src.volumeDb, pan: src.pan, sendAmount: src.sendAmount,
      };
    }
    if (!handle) {
      this.mixer.removeChannel(newId);
      return;
    }
    this.mixer.setVolume(newId, src.volumeDb);
    this.mixer.setMute(newId, src.muted);
    this.mixer.setPan(newId, src.pan);
    this.mixer.setSend(newId, src.sendAmount);
    this.layers.splice(idx + 1, 0, handle);
    this.cb.onLayersChanged(this.summaries());
  }

  /** Change an event layer's loop-length multiple (1×/2×/4×) after the fact. */
  setLayerMultiple(id: Id, multiple: LayerMultiple): void {
    const layer = this.layers.find((l) => l.id === id);
    if (!layer || layer.kind !== "event" || !layer.events || !layer.instrument) return;
    layer.multiple = multiple;
    const loopEnd = multiple > 1 ? multipleLoopEnd(this.transport.getBars(), multiple) : this.transport.loopEndPosition;
    layer.runtime.dispose();
    const runtime = new EventLayer(layer.instrument, layer.events, loopEnd);
    runtime.start(0);
    layer.runtime = runtime;
    this.cb.onLayersChanged(this.summaries());
  }

  dispose(): void {
    this.cancelRecording();
    for (const layer of this.layers) this.disposeLayer(layer);
    this.layers.length = 0;
  }

  // --- Serialization (persistence) ----------------------------------------
  serialize(): SerializedLayer[] {
    return this.layers.map((l) => {
      const base = {
        id: l.id, name: l.name, color: l.color,
        muted: l.muted, solo: l.solo, locked: l.locked, volumeDb: l.volumeDb, pan: l.pan, sendAmount: l.sendAmount,
        bars: l.multiple, // layer-length multiple (1 = base loop)
      };
      return l.kind === "audio"
        ? { ...base, kind: "audio" as const, audioRef: l.audioRef ?? "", durationSec: l.durationSec ?? 0 }
        : { ...base, kind: "event" as const, instrumentId: l.instrumentId ?? "synth-keys", events: l.events ?? [] };
    });
  }

  async restore(layers: readonly SerializedLayer[], loadBlob: (ref: string) => Promise<Blob | null>): Promise<void> {
    for (const s of layers) {
      try {
        if (s.kind === "event") this.restoreEventLayer(s);
        else await this.restoreAudioLayer(s, loadBlob);
      } catch {
        // skip a layer that fails to rebuild
      }
    }
    this.cb.onLayersChanged(this.summaries());
  }

  private restoreEventLayer(s: Extract<SerializedLayer, { kind: "event" }>): void {
    const channel = this.mixer.addChannel(s.id);
    const instrument = createInstrument(s.instrumentId);
    instrument.init(channel);
    instrument.setScale?.(this.key, this.scale);
    const multiple = (s.bars === 2 || s.bars === 4 ? s.bars : 1) as LayerMultiple;
    const loopEnd = multiple > 1 ? multipleLoopEnd(this.transport.getBars(), multiple) : this.transport.loopEndPosition;
    const runtime = new EventLayer(instrument, s.events, loopEnd);
    runtime.start(0);
    this.applyStripState(s);
    this.layers.push({
      id: s.id, name: s.name, color: s.color, kind: "event", multiple,
      runtime, instrument, instrumentId: s.instrumentId, events: s.events,
      muted: s.muted, solo: s.solo, locked: s.locked ?? false, volumeDb: s.volumeDb, pan: s.pan, sendAmount: s.sendAmount,
    });
  }

  private async restoreAudioLayer(
    s: Extract<SerializedLayer, { kind: "audio" }>,
    loadBlob: (ref: string) => Promise<Blob | null>,
  ): Promise<void> {
    const blob = await loadBlob(s.audioRef);
    if (!blob) return;
    const channel = this.mixer.addChannel(s.id);
    const { layer, peaks, durationSec } = await AudioLayer.fromBlob(blob, {
      loopDurSec: this.transport.loopDurationSec,
      latencyOffsetSec: this.latencyOffsetMs / 1000,
      output: channel,
    });
    layer.start(0);
    this.applyStripState(s);
    this.layers.push({
      id: s.id, name: s.name, color: s.color, kind: "audio", multiple: 1,
      runtime: layer, audioRef: s.audioRef, durationSec, peaks,
      muted: s.muted, solo: s.solo, locked: s.locked ?? false, volumeDb: s.volumeDb, pan: s.pan, sendAmount: s.sendAmount,
    });
  }

  private applyStripState(s: SerializedLayer): void {
    this.mixer.setVolume(s.id, s.volumeDb);
    this.mixer.setMute(s.id, s.muted);
    this.mixer.setSolo(s.id, s.solo);
    this.mixer.setPan(s.id, s.pan);
    this.mixer.setSend(s.id, s.sendAmount);
  }

  private clearWatchers(): void {
    this.loopUnsub?.();
    this.loopUnsub = null;
    this.countdownUnsub?.();
    this.countdownUnsub = null;
    if (this.freshStartId !== null) {
      this.transport.clearScheduled(this.freshStartId);
      this.freshStartId = null;
    }
  }

  private cancelRecording(): void {
    this.clearWatchers();
    this.recordingMic = null;
    this.loopFirstActive = false;
    this.state = "idle";
  }

  private disposeLayer(layer: LayerHandle, keepBlob = false): void {
    layer.runtime.dispose();
    layer.instrument?.dispose();
    this.mixer.removeChannel(layer.id);
    if (layer.audioRef && !keepBlob) this.persistence?.deleteBlob(layer.audioRef);
  }

  private summaries(): LayerSummary[] {
    const loopDur = this.transport.loopDurationSec;
    return this.layers.map((l) => ({
      id: l.id,
      name: l.name,
      color: l.color,
      kind: l.kind,
      muted: l.muted,
      solo: l.solo,
      locked: l.locked,
      multiple: l.multiple,
      volumeDb: l.volumeDb,
      pan: l.pan,
      sendAmount: l.sendAmount,
      marks:
        l.kind === "audio"
          ? (l.peaks ?? []).map((p, i, arr) => ({ pos: arr.length > 1 ? i / (arr.length - 1) : 0, velocity: p }))
          : (l.events ?? []).map((e) => ({
              pos: loopDur > 0 ? (e.time / (loopDur * l.multiple)) % 1 : 0,
              velocity: e.velocity,
            })),
    }));
  }

  private setState(state: LooperState): void {
    this.state = state;
    this.cb.onState(state);
  }
}
